import { useMemo, useState, useContext } from "react";
import {
  Box,
  Group,
  Stack,
  Text,
  TextInput,
  ScrollArea,
  Popover,
  UnstyledButton,
  ThemeIcon,
} from "@mantine/core";
import {
  IconChevronDown,
  IconSearch,
  IconCheck,
} from "@tabler/icons-react";
import { AuthContext } from "../contexts/AuthContext";
import { OrgTypes } from "../utils/constants";
import useListKeyboardNav from "../hooks/useListKeyboardNav";
import OrgLogo from "./admin/OrgLogo";
import { FlagIcon } from "./admin/CountryCell";
import classes from "../styles/OrgPicker.module.css";

const OrgContextBadge = () => {
  const { user, orgOptions, selectedOrgId, setSelectedOrgId } =
    useContext(AuthContext);

  const [opened, setOpened] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [query, setQuery] = useState("");
  const isStaff = user?.isStaff;

  const activeOrg = useMemo(
    () => orgOptions.find((o) => String(o.id) === String(selectedOrgId)),
    [orgOptions, selectedOrgId]
  );

  const groups = useMemo(() => {
    // Non-staff see only their own memberships, so a flat list (single
    // headerless group) is cleaner. Staff see every org, so grouping by type
    // helps them navigate the long list.
    if (!isStaff) {
      return [[null, orgOptions]];
    }
    const map = new Map();
    orgOptions.forEach((o) => {
      const group =
        String(o.orgType) === OrgTypes.CONTRACTOR
          ? "Contractors"
          : "Asset Owners";
      if (!map.has(group)) map.set(group, []);
      map.get(group).push(o);
    });
    return [...map.entries()];
  }, [orgOptions, isStaff]);

  const filteredGroups = useMemo(() => {
    if (!query.trim()) return groups;
    const q = query.trim().toLowerCase();
    return groups
      .map(([g, items]) => [
        g,
        items.filter((o) =>
          [o.fullName, o.shortName, o.countryCode]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q))
        ),
      ])
      .filter(([, items]) => items.length > 0);
  }, [groups, query]);

  // The groups flattened in render order, so the keyboard highlight can step
  // across group boundaries as one list.
  const flatOptions = useMemo(
    () => filteredGroups.flatMap(([, items]) => items),
    [filteredGroups]
  );
  const indexById = useMemo(
    () => new Map(flatOptions.map((o, i) => [String(o.id), i])),
    [flatOptions]
  );

  const handlePick = (org) => {
    setSelectedOrgId(String(org.id));
    setOpened(false);
    setQuery("");
  };

  const { activeIndex, handleKeyDown, getItemProps, inputRef } =
    useListKeyboardNav({
      opened,
      itemCount: flatOptions.length,
      initialIndex: Math.max(
        flatOptions.findIndex((o) => String(o.id) === String(selectedOrgId)),
        0
      ),
      onSelect: (i) => handlePick(flatOptions[i]),
      onClose: () => setOpened(false),
    });

  if (!activeOrg) return null;

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      offset={6}
      shadow="md"
      width={320}
      withinPortal
    >
      <Popover.Target>
        <UnstyledButton
          onClick={() => setOpened((v) => !v)}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          data-testid="org-context-badge"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            height: 42,
            padding: "0 12px 0 6px",
            maxWidth: 280,
            backgroundColor:
              opened || hovered
                ? "var(--mantine-color-default-hover)"
                : "transparent",
            border: "none",
            borderRadius: 8,
            color: "var(--mantine-color-text)",
            transition: "background-color 100ms",
          }}
        >
          <OrgLogo org={activeOrg} size={28} />
          <Box style={{ minWidth: 0, flex: 1 }}>
            <Text
              size="9px"
              fw={500}
              c="dimmed"
              tt="uppercase"
              style={{ letterSpacing: "0.06em", lineHeight: 1.1 }}
            >
              Working As
            </Text>
            <Group gap={5} wrap="nowrap" align="center" mt={2}>
              <Text
                size="xs"
                fw={500}
                truncate
                style={{ lineHeight: 1.1, minWidth: 0 }}
              >
                {activeOrg.fullName || activeOrg.shortName}
              </Text>
            </Group>
          </Box>
          <IconChevronDown size={12} style={{ flexShrink: 0 }} />
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown p={6}>
        <Box px={4} pt={2} pb={6}>
          <TextInput
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            onKeyDown={handleKeyDown}
            placeholder="Switch organisation"
            size="xs"
            leftSection={<IconSearch size={12} />}
            role="combobox"
            aria-expanded={opened}
            aria-controls="org-context-listbox"
            aria-activedescendant={
              flatOptions[activeIndex]
                ? `org-context-option-${flatOptions[activeIndex].id}`
                : undefined
            }
          />
        </Box>
        <ScrollArea.Autosize mah={360} type="auto">
          <Stack gap={2} id="org-context-listbox" role="listbox">
            {filteredGroups.length === 0 && (
              <Text size="xs" c="dimmed" ta="center" py="md">
                No matches for "{query}"
              </Text>
            )}
            {filteredGroups.map(([group, items]) => (
              <Box key={group || "all"}>
                {group && (
                  <Text
                    size="10px"
                    fw={500}
                    c="dimmed"
                    tt="uppercase"
                    style={{ letterSpacing: "0.06em", padding: "6px 10px 4px" }}
                  >
                    {group}
                  </Text>
                )}
                {items.map((o) => {
                  const sel = String(o.id) === String(selectedOrgId);
                  return (
                    <UnstyledButton
                      key={o.id}
                      id={`org-context-option-${o.id}`}
                      role="option"
                      aria-selected={sel}
                      onClick={() => handlePick(o)}
                      data-testid={`org-context-option-${o.id}`}
                      data-selected={sel || undefined}
                      className={classes.option}
                      {...getItemProps(indexById.get(String(o.id)))}
                    >
                      <OrgLogo org={o} size={26} />
                      <Box style={{ minWidth: 0 }}>
                        <Text size="xs" fw={500} truncate>
                          {o.fullName}
                        </Text>
                        <Group gap={6} mt={2} wrap="nowrap" align="center">
                          {o.countryCode && (
                            <Group gap={4} wrap="nowrap" align="center">
                              <FlagIcon code={o.countryCode} size={9} />
                              <Text size="10px" c="dimmed">
                                {o.countryCode}
                              </Text>
                            </Group>
                          )}
                          {o.orgType && (
                            <>
                              {o.countryCode && (
                                <Text size="10px" c="dimmed">
                                  ·
                                </Text>
                              )}
                              <Text size="10px" c="dimmed">
                                {String(o.orgType).replace(/_/g, " ")}
                              </Text>
                            </>
                          )}
                        </Group>
                      </Box>
                      {sel && (
                        <ThemeIcon
                          size="xs"
                          variant="transparent"
                          color="teal"
                        >
                          <IconCheck size={12} />
                        </ThemeIcon>
                      )}
                    </UnstyledButton>
                  );
                })}
              </Box>
            ))}
          </Stack>
        </ScrollArea.Autosize>
      </Popover.Dropdown>
    </Popover>
  );
};

export default OrgContextBadge;
